// Locale primitives split out of `shared.ts` so consumers (both client and
// server) can pull just these without dragging the whole zod-heavy barrel
// into their bundle.

export const locales = ['es-ES', 'ca-ES', 'en-US', 'pt-BR'] as const

export type Locale = (typeof locales)[number]

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (locales as readonly string[]).includes(value)

// Pick the best matching supported `Locale` from an `accept-language` header.
// Implements the parts of RFC 9110 we actually use: comma-separated entries,
// optional `;q=` weights, descending sort by q, exact + language-prefix match.
export function parseLocale(headers: Record<string, string | string[] | undefined>): Locale {
  const raw = headers['accept-language']
  const value = Array.isArray(raw) ? raw[0] : raw
  if (!value) return 'es-ES'

  const tags: { tag: string; q: number }[] = []
  for (const part of value.split(',')) {
    const [lang, ...params] = part.trim().split(';')
    if (!lang) continue
    let q = 1
    for (const p of params) {
      const m = /q=([\d.]+)/.exec(p.trim())
      if (m?.[1]) q = parseFloat(m[1])
    }
    tags.push({ tag: lang.toLowerCase(), q })
  }
  tags.sort((a, b) => b.q - a.q)

  for (const { tag } of tags) {
    const exact = (locales as readonly string[]).find((l) => l.toLowerCase() === tag)
    if (exact) return exact as Locale
    const prefix = tag.split('-')[0]
    const prefixMatch = (locales as readonly string[]).find((l) => l.toLowerCase().startsWith(`${prefix}-`))
    if (prefixMatch) return prefixMatch as Locale
  }
  return 'es-ES'
}
